import { List, message, Segmented, Skeleton } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FILTER_OPTION_URL, MAGAZINE_FAVORITE_URL, MAGAZINE_LIKE_URL } from '../../constants/Url';
import AppDownloadModal from '../../components/AppDownloadModal';
import CustomPagination from '../../components/common/CustomPagination';
import httpClient from '../../utils/HttpClient';
import { objectToQuery } from '../../utils/Utils';
import { debounce } from 'lodash';
import MagazineModal from '../../components/MagazineModals';
import { useDispatch, useSelector } from 'react-redux';
import { setMagazineFavListAction, setYearsAction } from '../../modules/dataWebsite/actions';
import { loginSuccessAction } from '../../modules/auth/actions';
import { storageKeys } from '../../constants/storage';
import { useTranslation } from 'react-i18next';
import {
    HeartOutlined,
    HeartFilled,
    StarOutlined,
    StarFilled
} from '@ant-design/icons';
import AuthModal from '../../components/auth/AuthModal';
import PROModal from '../../components/PROModal';
import { useMemberAccess } from '../../hooks/useMemberAccess';

export default function FavMagazinePage() {
    const dispatch = useDispatch();
    const authModalRef = useRef()
    const user = useSelector((state) => state.authReducer);
    let token = user && user?.token ? user?.token : null
    const dataSource = useSelector((state) => state.dataWebsiteReducer?.magazineFavData);
    const yearsData = useSelector((state) => state.dataWebsiteReducer?.yearsData);
    const [alignValue, setAlignValue] = useState('ALL');
    const [typeValue, setTypeValue] = useState('艾絲 Estetica');
    const [tagValue, setTagValue] = useState('ALL');
    const appDownloadRef = useRef();
    const [loading, setLoading] = useState(false);
    const [pageNo, setPageNo] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const { t } = useTranslation();
    const [total, setTotal] = useState(0);
    const { hasNoActiveMembership } = useMemberAccess();
    const [isMemberGateOpen, setIsMemberGateOpen] = useState(false);

    const handleShowDownloadApp = (item) => {
        if (token) {
            if (user?.user?.completedTS) {
                // Gate: expired trial or no membership cannot read magazine
                if (hasNoActiveMembership()) {
                    setIsMemberGateOpen(true);
                    return;
                }
                appDownloadRef.current?.showModal(item);
            } else {
                authModalRef.current.open("REGISTER")
            }
        } else {
            authModalRef.current.open()
        }
    }

    const fetchDataOptions = async () => {
        try {
            const res = await httpClient.get(FILTER_OPTION_URL)
                .then(res => res.data)
                .catch(error => ({ status: 500, message: error?.message }));

            if (res.status === 200) {
                dispatch(setYearsAction(res?.data ? res.data : []));
                if (res?.data?.magazineType?.length > 0) {
                    setTypeValue(prev => (prev && res.data.magazineType.includes(prev) ? prev : res.data.magazineType[0]));
                }
            } else {
                dispatch(setYearsAction([]));
            }
        } catch (error) {
            console.error("fetchDataOptions -> error", error)
        }
    }

    useEffect(() => {
        fetchDataOptions();
    }, [])

    const loadinFirst = useRef(false)
    const fetchData = async () => {
        try {
            if (!loadinFirst?.current && dataSource?.length > 0) {
                setLoading(false);
            } else {
                setLoading(true);
            }
            loadinFirst.current = true
            const query = {
                releaseYear: alignValue,
                type: typeValue,
                tag: tagValue,
                pageNo: pageNo,
                pageSize: pageSize
            };

            const res = await httpClient.get(objectToQuery(MAGAZINE_FAVORITE_URL, query))
                .then(res => res.data)
                .catch(error => ({ status: 500, message: error?.message }));

            if (res.status === 200) {
                dispatch(setMagazineFavListAction(res?.data ? res.data : []));
                setTotal(res.total);
            } else {
                dispatch(setMagazineFavListAction([]));
                setTotal(0);
            }
        } catch (error) {
            console.error("fetchData -> error", error)
        } finally {
            setLoading(false);
        }
    }

    const debouncedFetchData = useCallback(
        debounce(fetchData, 300),
        [pageNo, pageSize, alignValue, typeValue, tagValue, token]
    );

    useEffect(() => {
        debouncedFetchData();
        return debouncedFetchData.cancel;
    }, [debouncedFetchData])

    const handleLike = async (item) => {
        try {
            setLoading(true);
            const res = !item?.isLike ? await httpClient.post(MAGAZINE_LIKE_URL, {
                "magazineId": item?.magazineId
            })
                .then(res => res.data)
                .catch(error => ({ status: 500, error })) : await httpClient.delete(MAGAZINE_LIKE_URL, {
                    data: { magazineId: item?.magazineId }
                })
                    .then(res => res.data)
                    .catch(error => ({ status: 500, error }))

            if (res?.status === 200) {
                message.success(t(res?.message || "Successfully"));
                await fetchData();
            } else {
                message.error(t(res?.error?.message || res?.message || "Failed"));
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            message.error(t("Failed"));
        }
    }

    const handleFavorite = async (item) => {
        try {
            setLoading(true);
            const res = !item?.isFavorite ? await httpClient.post(MAGAZINE_FAVORITE_URL, {
                "magazineId": item?.magazineId
            })
                .then(res => res.data)
                .catch(error => ({ status: 500, error })) : await httpClient.delete(MAGAZINE_FAVORITE_URL, {
                    data: { magazineId: item?.magazineId }
                })
                    .then(res => res.data)
                    .catch(error => ({ status: 500, error }))
            if (res?.status === 200) {
                await fetchData();
                message.success(t(res?.message || "Successfully"));
            } else {
                message.error(t(res?.error?.message || res?.message || "Failed"));
            }
            setLoading(false);
        } catch (error) {
            message.error(t("Failed"));
            setLoading(false);
        }
    }

    const handleTypeChange = (value) => {
        setTypeValue(value);
        setPageNo(1);
    };
    const handleYearChange = (value) => {
        setAlignValue(value);
        setPageNo(1);
    };
    const handleTagChange = (value) => {
        setTagValue(value._id);
        setPageNo(1);
    };

    const yearsOptions = yearsData?.releaseYear?.length 
        ? (yearsData.releaseYear.includes('ALL') ? yearsData.releaseYear : ['ALL', ...yearsData.releaseYear])
        : [];

    const tagsOptions = yearsData?.tags?.length
        ? [
            { _id: "ALL", name: "ALL" },
            ...yearsData.tags,
        ]
        : [];

    const typeOptions = yearsData?.magazineType?.length
        ? yearsData.magazineType
        : [];

    return (
        <div className='esi-std-container full-flex pb-[40px] px-4 md:px-0'>
            <AuthModal ref={authModalRef} /> 
            
            {/* Header Tabs */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-100 pb-3 mt-6 mb-6 gap-4">
                <h1 className="text-[24px] font-bold text-[#111] m-0">{t("text_favorite_magazine")}</h1>
                <div className="flex gap-8">
                    {typeOptions.map((opt) => (
                        <div
                            key={opt}
                            className={`cursor-pointer pb-2 text-[16px] font-medium relative transition-colors ${
                                typeValue === opt 
                                    ? 'text-[#111] font-semibold font-bold' 
                                    : 'text-gray-400 hover:text-gray-600'
                            }`}
                            onClick={() => handleTypeChange(opt)}
                        >
                            {t(opt)}
                            {typeValue === opt && (
                                <div className="absolute bottom-0 left-0 right-0 h-[3px] rounded-full" style={{background:"linear-gradient(to right, #50c7f8, #7887fd, #df97fb)"}}/>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Year Filter */}
            <div 
                className="flex items-center gap-4 mb-4 overflow-x-auto whitespace-nowrap py-1"
                style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}
            >
                <span className="text-[14px] font-medium text-gray-500 min-w-[50px]">{t("text_year_magazine")}:</span>
                <div className="flex gap-2">
                    {yearsOptions.map((year) => {
                        const isSelected = alignValue === year;
                        return (
                            <button
                                key={year}
                                onClick={() => handleYearChange(year)}
                                className={`px-4 py-1 text-[13px] font-medium transition-all duration-200 border rounded-[2px] cursor-pointer ${
                                    isSelected
                                        ? 'bg-[#000000] border-black text-white'
                                        : 'bg-white border-gray-200 text-[#333333] hover:border-gray-400'
                                }`}
                            >
                                {year === 'ALL' ? t("category_all") : year}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Tag Filter */}
            <div className="flex items-start gap-4 mb-6">
                <span className="text-[14px] font-medium text-gray-500 min-w-[50px] py-1">{t("text_tag")}:</span>
                <div className="flex flex-wrap gap-2">
                    {tagsOptions.map((tag) => {
                        const isSelected = tagValue === tag._id;
                        return (
                            <button
                                key={tag._id}
                                onClick={() => handleTagChange(tag)}
                                className={`px-4 py-1 text-[13px] font-medium transition-all duration-200 rounded-[2px] cursor-pointer ${
                                    isSelected
                                        ? 'bg-[#e7e8e9] border-transparent text-[#333333] hover:bg-gray-200'
                                        : 'text-[#333333] hover:bg-gray-200'
                                }`}
                            >
                                {tag._id === "ALL" ? t("category_all") : tag.name}
                            </button>
                        );
                    })}
                </div>
            </div>

            <List
                className="esi-std-white-space full-flex"
                rootClassName='loading-full-flex'
                grid={{
                    gutter: [25, 15],
                    xs: 2,
                    sm: 2,
                    md: 3,
                    lg: 4,
                    xl: 4,
                    xxl: 4,
                }}
                dataSource={loading ? Array(8).fill(0) : dataSource}
                renderItem={(item) => (
                    loading ?
                        <List.Item>
                            <div className="select-none text-center gap-3 flex flex-col">
                                <div className="overflow-hidden">
                                    <Skeleton.Image rootClassName='esi-card-image' style={{ cursor: "pointer" }} className='esi-card-image' active={true} />
                                </div>
                                <Skeleton.Input className="esi-card-title max-w-[100%] truncate" active block />
                            </div>
                        </List.Item>
                        :
                        <List.Item>
                            <div className="select-none text-center gap-3 flex flex-col">
                                <div
                                    className="overflow-hidden"
                                    style={{ position: "relative" }}
                                >
                                    <img
                                        onClick={() => handleShowDownloadApp(item)}
                                        className="esi-card-image" style={{ cursor: "pointer" }} src={item?.picUrl} alt={item.previewFile?.fileName} />
                                    {token ? <div
                                        style={{ position: "absolute", bottom: "0px", fontSize: "18px", background: "#ffffff7d", width: "100%" }}
                                    >
                                        {token ? item?.isFavorite ? <span onClick={() => handleFavorite(item)} style={{ cursor: "pointer", color: "red" }} className='ms-2-'>
                                            <StarFilled />
                                            <span>{t("text_favorite")}</span>
                                        </span> : <span onClick={() => handleFavorite(item)} style={{ cursor: "pointer" }} className='ms-2-'>
                                            <StarOutlined /> 
                                            <span>{t("text_favorite")}</span>
                                        </span> : ""}

                                    </div> : ""}
                                </div>
                                <div className="esi-card-title max-w-[100%] truncate">{item.title}</div>
                            </div>
                        </List.Item>
                )}
            ></List>
            
            {(loading || total > 0 || (dataSource && dataSource.length > 0)) && (
                <div className="flex justify-end mt-10">
                    <CustomPagination
                        current={pageNo}
                        pageSize={pageSize}
                        total={loading ? 8 : (total || dataSource?.length || 0)}
                        pageSizeOptions={[12, 24, 36, 48, 60]}
                        onChange={(page, limit) => {
                            if (pageSize !== limit) {
                                setPageSize(limit);
                                setPageNo(1);
                            } else {
                                setPageNo(page);
                            }
                        }}
                    />
                </div>
            )}
            
            <MagazineModal ref={appDownloadRef} />
            <PROModal open={isMemberGateOpen} onCancel={() => setIsMemberGateOpen(false)} />
        </div>
    )
}
